import { useState } from "react";
import { useShelterHistory, type ShelterHistoryEntry } from "@/hooks/useShelterHistory";
import { useShelters } from "@/hooks/useShelters";
import { useRevertUpdate, type ShelterUpdateWithOriginal } from "@/hooks/useShelterUpdates";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Clock, Check, X, Search, Filter, ChevronDown, Undo2 } from "lucide-react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { UpdateDiffView } from "@/components/UpdateDiffView";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Convert ShelterHistoryEntry to ShelterUpdateWithOriginal for diff view
// For history view, we need to use original_values to show what the data was BEFORE this update
// (not the current shelter state which may have changed from later updates)
const toUpdateWithOriginal = (
  entry: ShelterHistoryEntry, 
  isNewShelterCreation: boolean
): ShelterUpdateWithOriginal => {
  // For new shelter creations, no original to compare
  if (isNewShelterCreation) {
    return { ...entry, originalShelter: undefined };
  }
  
  // If we have original_values, use them to build a "virtual" originalShelter
  // This shows what the shelter looked like BEFORE this specific update was approved
  if (entry.original_values && Object.keys(entry.original_values).length > 0) {
    // The keys in original_values tell us which fields actually changed in this update.
    // For changed fields: use original_values (the BEFORE value)
    // For unchanged fields: use entry value (same as before = same as after, no diff shown)
    const changedKeys = new Set(Object.keys(entry.original_values));
    
    // Helper: if field was changed, return original value; otherwise return entry value
    // This ensures unchanged fields show the same value in "before" and "after" = no diff
    const getOriginalValue = <T,>(key: string, entryValue: T): T => {
      if (changedKeys.has(key)) {
        return entry.original_values[key] as T;
      }
      return entryValue; // unchanged - use same value to avoid false diff
    };
    
    const virtualOriginal = {
      id: entry.shelter_id || "",
      name: getOriginalValue("name", entry.shelter_name),
      city: getOriginalValue("city", entry.city),
      region: getOriginalValue("region", entry.region),
      address: getOriginalValue("address", entry.address),
      postal_code: getOriginalValue("postal_code", null),
      lat: getOriginalValue("lat", entry.lat),
      lng: getOriginalValue("lng", entry.lng),
      phone: getOriginalValue("phone", entry.phone),
      email: getOriginalValue("email", entry.email),
      website: getOriginalValue("website", entry.website),
      current_needs: getOriginalValue("current_needs", entry.current_needs),
      urgent_needs: getOriginalValue("urgent_needs", entry.urgent_needs),
      urgent_needs_set_at: null,
      dog_walking_available: getOriginalValue("dog_walking_available", entry.dog_walking_available),
      volunteering_available: getOriginalValue("volunteering_available", entry.volunteering_available),
      dogs_available: getOriginalValue("dogs_available", entry.dogs_available),
      cats_available: getOriginalValue("cats_available", entry.cats_available),
      volunteer_info: getOriginalValue("volunteer_info", entry.volunteer_info),
      description: getOriginalValue("description", entry.description),
      last_updated: entry.created_at,
      created_at: entry.created_at,
      data_verified: false,
      organization_verified: false,
      organization_verified_at: null,
      organization_verification_pending: false,
      organization_verification_requested_at: null,
      contacted: false,
    };
    
    return { ...entry, originalShelter: virtualOriginal };
  }
  
  // Fallback: no original_values recorded - can't show proper diff
  // This happens for old entries before we started saving original_values
  return { ...entry, originalShelter: entry.originalShelter };
};

export const ShelterHistoryTab = () => {
  const { data: history, isLoading } = useShelterHistory(true);
  const { data: shelters } = useShelters();
  const revertUpdate = useRevertUpdate();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "approved" | "rejected">("all");
  const [shelterFilter, setShelterId] = useState<string>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRevert = async (entry: ShelterHistoryEntry) => {
    try {
      await revertUpdate.mutateAsync(entry);
      toast.success("Zmiana została cofnięta. Przywrócono poprzednie wartości.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Wystąpił błąd podczas cofania zmiany");
    }
  };

  // Check if an entry can be reverted
  const canRevert = (entry: ShelterHistoryEntry): boolean => {
    return (
      entry.status === "approved" && 
      !!entry.shelter_id && 
      !!entry.original_values && 
      Object.keys(entry.original_values).length > 0
    );
  };

  // Field labels for displaying original values
  const fieldLabels: Record<string, string> = {
    name: "Nazwa",
    city: "Miasto",
    region: "Województwo",
    address: "Adres",
    phone: "Telefon",
    email: "Email",
    website: "Strona www",
    current_needs: "Aktualne potrzeby",
    urgent_needs: "Pilne potrzeby",
    dog_walking_available: "Spacery z psami",
    volunteering_available: "Wolontariat",
    dogs_available: "Psy do adopcji",
    cats_available: "Koty do adopcji",
    volunteer_info: "Info o wolontariacie",
    description: "Opis",
  };

  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Tak" : "Nie";
    return String(value);
  };

  // Filter history
  const filteredHistory = history?.filter(entry => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      entry.shelter_name.toLowerCase().includes(searchLower) ||
      entry.city.toLowerCase().includes(searchLower) ||
      entry.current_needs?.toLowerCase().includes(searchLower);

    // Status filter
    const matchesStatus = statusFilter === "all" || entry.status === statusFilter;

    // Shelter filter
    const matchesShelter = shelterFilter === "all" || entry.shelter_id === shelterFilter;

    return matchesSearch && matchesStatus && matchesShelter;
  }) || [];

  // Group by shelter for better visualization
  const groupedByShelter = filteredHistory.reduce((acc, entry) => {
    const key = entry.shelter_id || "new";
    if (!acc[key]) {
      acc[key] = {
        shelterName: entry.shelter_current_name || entry.shelter_name,
        entries: [],
      };
    }
    acc[key].entries.push(entry);
    return acc;
  }, {} as Record<string, { shelterName: string; entries: typeof filteredHistory }>);

  // Find the oldest approved entry for each shelter (this is the "creation" entry)
  const oldestApprovedByShelterId = new Map<string, string>();
  filteredHistory.forEach(entry => {
    if (entry.status === "approved" && entry.shelter_id) {
      const existing = oldestApprovedByShelterId.get(entry.shelter_id);
      if (!existing) {
        // Find the oldest approved entry for this shelter from all entries (not just filtered)
        const allEntriesForShelter = history?.filter(e => 
          e.shelter_id === entry.shelter_id && e.status === "approved"
        ) || [];
        const oldest = allEntriesForShelter.reduce((oldest, e) => 
          new Date(e.created_at) < new Date(oldest.created_at) ? e : oldest
        , allEntriesForShelter[0]);
        if (oldest) {
          oldestApprovedByShelterId.set(entry.shelter_id, oldest.id);
        }
      }
    }
  });

  // Check if an entry is a "new shelter creation" (first approved entry for that shelter)
  // Admin edits are never "new shelter creations" — they edit existing shelters
  const isNewShelterCreation = (entry: ShelterHistoryEntry): boolean => {
    if (entry.source === "admin_edit") return false;
    if (!entry.shelter_id) return true; // No shelter_id means it was a new shelter that wasn't linked
    return oldestApprovedByShelterId.get(entry.shelter_id) === entry.id;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg">Historia zmian</h2>
        <Badge variant="secondary">
          {history?.length || 0} wpisów
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj po nazwie, mieście..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-full sm:w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie</SelectItem>
            <SelectItem value="approved">Zatwierdzone</SelectItem>
            <SelectItem value="rejected">Odrzucone</SelectItem>
          </SelectContent>
        </Select>
        <Select value={shelterFilter} onValueChange={setShelterId}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="Schronisko" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie schroniska</SelectItem>
            {shelters?.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grouped history */}
      {Object.keys(groupedByShelter).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(groupedByShelter).map(([shelterId, { shelterName, entries }]) => (
            <Card key={shelterId}>
              <Collapsible 
                open={expandedIds.has(shelterId)} 
                onOpenChange={() => toggleExpanded(shelterId)}
              >
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {shelterName}
                          <Badge variant="outline" className="ml-2">
                            {entries.length} {entries.length === 1 ? "wpis" : entries.length < 5 ? "wpisy" : "wpisów"}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Ostatnia zmiana: {format(new Date(entries[0].reviewed_at || entries[0].created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                        </CardDescription>
                      </div>
                      <Button variant="ghost" size="sm">
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedIds.has(shelterId) ? "rotate-180" : ""}`} />
                      </Button>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      {entries.map((entry, index) => (
                        <div 
                          key={entry.id}
                          className={`p-3 rounded-lg border ${
                            entry.status === "approved" 
                              ? "bg-green-50/60 dark:bg-green-950/10 border-green-200/60 dark:border-green-800/40" 
                              : "bg-red-50/60 dark:bg-red-950/10 border-red-200/60 dark:border-red-800/40"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                <Badge 
                                  variant={entry.status === "approved" ? "default" : "destructive"}
                                  className="text-xs"
                                >
                                  {entry.status === "approved" ? (
                                    <><Check className="w-3 h-3 mr-1" />Zatwierdzone</>
                                  ) : (
                                    <><X className="w-3 h-3 mr-1" />Odrzucone</>
                                  )}
                                </Badge>
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    entry.source === "admin_edit" 
                                      ? "text-purple-600 border-purple-300/60 bg-purple-50/50 dark:bg-purple-950/20" 
                                      : "text-emerald-600 border-emerald-300/60 bg-emerald-50/50 dark:bg-emerald-950/20"
                                  }`}
                                >
                                  {entry.source === "admin_edit" ? "Panel admina" : "Zgłoszenie zewnętrzne"}
                                </Badge>
                                {!entry.shelter_id && (
                                  <Badge variant="secondary" className="text-xs">Nowe schronisko</Badge>
                                )}
                                {entry.urgent_needs && (
                                  <Badge variant="outline" className="text-xs text-amber-700/60 border-amber-300/40">Pilne</Badge>
                                )}
                                {canRevert(entry) && (
                                  <Badge variant="outline" className="text-xs text-blue-600/70">Można cofnąć</Badge>
                                )}
                              </div>
                            </div>
                            
                            {/* Revert button */}
                            {canRevert(entry) && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="shrink-0 text-amber-700/60 border-amber-300/40 hover:bg-amber-50/40"
                                    disabled={revertUpdate.isPending}
                                  >
                                    <Undo2 className="w-3 h-3 mr-1" />
                                    Cofnij
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="max-w-lg">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Cofnąć tę zmianę?</AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="space-y-3">
                                        <p>
                                          Ta operacja przywróci poprzednie wartości pól w schronisku "{entry.shelter_current_name || entry.shelter_name}". 
                                          Zgłoszenie zostanie oznaczone jako odrzucone.
                                        </p>
                                        
                                        {/* Preview of values to be restored */}
                                        <div className="mt-4 p-3 bg-muted rounded-lg border">
                                          <p className="text-xs font-medium text-foreground mb-2">Wartości do przywrócenia:</p>
                                          <div className="space-y-1.5">
                                            {entry.original_values && Object.entries(entry.original_values).map(([key, value]) => (
                                              <div key={key} className="flex items-start gap-2 text-xs">
                                                <span className="font-medium text-foreground min-w-[120px]">
                                                  {fieldLabels[key] || key}:
                                                </span>
                                                <span className="text-muted-foreground">
                                                  {formatValue(key, value)}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Anuluj</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleRevert(entry)}
                                      className="bg-amber-700/70 hover:bg-amber-700/80"
                                    >
                                      Tak, cofnij zmianę
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                          
                          <p className="text-sm font-medium mt-2">
                            Zgłoszona nazwa: {entry.shelter_name}
                          </p>
                          
                          {entry.current_needs && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              Potrzeby: {entry.current_needs}
                            </p>
                          )}

                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Zgłoszono: {format(new Date(entry.created_at), "d MMM, HH:mm", { locale: pl })}
                            </span>
                            {entry.reviewed_at && (
                              <span className="flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                Rozpatrzono: {format(new Date(entry.reviewed_at), "d MMM, HH:mm", { locale: pl })}
                              </span>
                            )}
                          </div>

                          {entry.submitter_note && (
                            <p className="text-xs mt-2 p-2 bg-amber-100 dark:bg-amber-900/30 rounded text-amber-800 dark:text-amber-200">
                              💬 {entry.submitter_note}
                            </p>
                          )}

                          {/* Diff view - similar to pending updates */}
                          <div className="mt-3 pt-3 border-t border-border/50">
                            <UpdateDiffView update={toUpdateWithOriginal(entry, isNewShelterCreation(entry))} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">Brak historii zmian</p>
            <p className="text-sm">Zatwierdzone i odrzucone zgłoszenia pojawią się tutaj</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
